import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { requestAgencyMembership, fetchAgencies } from "../services/api";
import "./AgencyRequest.css";
import axios from 'axios';
import { toast } from 'react-toastify';

const AgencyRequest = () => {
  const { currentUser, hasPendingAgencyRequest, getUserId, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.redirectTo || "/enhanced-dashboard";
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [disabledAgencies, setDisabledAgencies] = useState({});
  const [formData, setFormData] = useState({
    agencyId: "",
    requestMessage: "",
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      // Check for pending or rejected agency requests in localStorage
      try {
        const userJson = localStorage.getItem('user');
        const agencyRequestsJson = localStorage.getItem('agencyRequests');
        const userId = getUserId();
        
        if (userJson) {
          const user = JSON.parse(userJson);
          
          // If user has an agency with pending status, show appropriate message
          if (user.agencyId && user.agencyStatus === 'pending') {
            setError(
              "You already have a pending agency request. Please wait for approval."
            );
            
            // Set the disabled agency so they can't select it again
            setDisabledAgencies({
              [user.agencyId]: "You already have a pending request for this agency"
            });
          }
          
          // Check for rejected requests and previous requests in localStorage
          if (agencyRequestsJson && userId) {
            const requests = JSON.parse(agencyRequestsJson);
            const agencyStatusMap = {};
            
            // Process each request to find rejected ones
            requests.forEach(req => {
              if (req.userId === userId) {
                if (req.status === 'rejected') {
                  // Rejected requests can be resubmitted
                  agencyStatusMap[req.agencyId] = null;
                } else if (req.status === 'pending') {
                  // Pending requests can't be resubmitted
                  agencyStatusMap[req.agencyId] = "You already have a pending request for this agency";
                }
              }
            });
            
            setDisabledAgencies(prevState => ({
              ...prevState,
              ...agencyStatusMap
            }));
          }
        }
      } catch (err) {
        console.error("Error checking agency status:", err);
      }
      
      // Fetch agencies from API
      try {
        const token = localStorage.getItem('token');
        
        // First try the public agencies endpoint
        try {
          const publicUrl = `${window.location.origin}/api/agencies/public`;
          console.log('Trying to fetch public agencies from:', publicUrl);
          
          const res = await axios.get(publicUrl);
          setAgencies(res.data);
          console.log("Loaded agencies from public endpoint:", res.data);
          setError(null); // Clear any previous error
          setLoading(false);
          return; // Exit the function if successful
        } catch (publicErr) {
          console.error("Error fetching from public endpoint:", publicErr);
          // Continue to try the API service or protected endpoint
        }
        
        // Try using the API service method next
        try {
          const agenciesData = await fetchAgencies();
          setAgencies(agenciesData);
          console.log("Loaded agencies via API service:", agenciesData);
          setError(null); // Clear any previous error
          setLoading(false);
          return; // Exit the function if successful
        } catch (apiErr) {
          console.error("Error fetching agencies via API service:", apiErr);
          // Try the protected endpoint as last resort
        }
        
        // As a last resort, try the protected endpoint with auth
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        
        const res = await axios.get('/api/agencies', config);
        setAgencies(res.data);
        console.log("Loaded agencies from protected endpoint:", res.data);
        setError(null); // Clear any previous error
      } catch (err) {
        console.error("Error fetching agencies:", err);
        // Check if we have any agencies already (might happen from a partial load)
        if (agencies.length === 0) {
          // If no agencies loaded at all, provide default fallback data
          const fallbackAgencies = [
            { _id: "fallback1", name: "AeroIndustrial Property Group" },
            { _id: "fallback2", name: "Global Industrial Park Management" },
            { _id: "fallback3", name: "Heritage Properties Inc." },
            { _id: "fallback4", name: "Malaysia Industrial Network" },
            { _id: "fallback5", name: "Commercial Estates Agency" }
          ];
          setAgencies(fallbackAgencies);
          console.log("Using fallback agencies due to API error");
        }
        setError("Note: Using backup agency data. You can still submit your request.");
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [hasPendingAgencyRequest, agencies.length, getUserId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // If they select an agency, check if it's disabled
    if (name === 'agencyId' && disabledAgencies[value]) {
      setError(disabledAgencies[value]);
    } else {
      // Clear error if they select a valid agency
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.agencyId) {
      setError("Please select an agency");
      return;
    }

    if (!formData.requestMessage) {
      setError("Please enter a message");
      return;
    }
    
    // Check if the selected agency is disabled
    if (disabledAgencies[formData.agencyId]) {
      setError(disabledAgencies[formData.agencyId]);
      return;
    }

    // Check if user already has an agency membership or pending request
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const userData = JSON.parse(userJson);
        
        // If user is already a member of an approved agency, they can't request another
        if (userData.agencyId && userData.agencyStatus === 'approved') {
          setError("You are already a member of an agency. Please contact support if you need to change agencies.");
          return;
        }
        
        // If user has a pending request for a different agency
        if (userData.agencyId && 
            userData.agencyId !== formData.agencyId && 
            userData.agencyStatus === 'pending') {
          setError("You already have a pending request for a different agency. Please wait for that request to be processed.");
          return;
        }
      }
    } catch (err) {
      console.error("Error checking existing agency status:", err);
    }

    setError(null);
    setSubmitting(true);

    try {
      // Find the selected agency to get name (may be useful for display)
      const selectedAgency = agencies.find(a => a._id === formData.agencyId);
      console.log('Selected agency:', selectedAgency);
      
      // Direct API call to request agency membership
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      // Use standard axios post to avoid any issues with the imported function
      const response = await axios.post('/api/users/request-agency', formData, config);
      console.log('Agency request response:', response.data);
      
      setSuccess(true);
      setFormData({
        agencyId: "",
        requestMessage: "",
      });

      // Refresh the user to update UI
      if (typeof refreshUser === 'function') {
        await refreshUser();
      } else {
        console.warn('refreshUser is not a function, skipping refresh');
      }

      // Redirect after success message
      setTimeout(() => {
        navigate(redirectTo);
      }, 3000);
    } catch (err) {
      console.error("Error submitting agency request:", err);
      setError(
        err.response?.data?.message || "Failed to submit request. Please try again."
      );
      toast.error('Failed to submit agency request');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="agency-request-container">
        <div className="success-message">
          <h2>Request Submitted Successfully!</h2>
          <p>
            Your request to join the agency has been submitted. The agency
            administrator will review your request and get back to you soon.
          </p>
          <p>You will be redirected shortly...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="agency-request-container">
      <div className="agency-request-card">
        <h2>Request Agency Membership</h2>
        <p>
          Join an agency to access exclusive industrial property listings and
          streamline your booking process.
        </p>

        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading-message">Loading agencies...</div>
        ) : (
          <form onSubmit={handleSubmit} className="agency-request-form">
            <div className="form-group">
              <label htmlFor="agencyId">Select Agency</label>
              <select
                id="agencyId"
                name="agencyId"
                value={formData.agencyId}
                onChange={handleChange}
                disabled={submitting}
              >
                <option value="">Select an agency</option>
                {agencies.map((agency) => (
                  <option 
                    key={agency._id} 
                    value={agency._id}
                    disabled={!!disabledAgencies[agency._id]}
                  >
                    {agency.name} {disabledAgencies[agency._id] ? '(Pending Approval)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="requestMessage">Message (Optional)</label>
              <textarea
                id="requestMessage"
                name="requestMessage"
                value={formData.requestMessage}
                onChange={handleChange}
                placeholder="Tell us about your interest in joining this agency"
                rows="4"
                disabled={submitting}
              ></textarea>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={() => navigate("/enhanced-dashboard")}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-button"
                disabled={submitting || (formData.agencyId && !!disabledAgencies[formData.agencyId])}
              >
                {submitting ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AgencyRequest;
